import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { getIsSignatureDetectionPending, MimeType } from '@aline/common';
import { Button, Chip, Collapse, Divider, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import { ShieldAlert, ShieldCheck, Signature } from 'lucide-react';
import { AlineTooltip, AnimatedArrowIcon, ConfirmationDialog, Spacer } from 'src/components';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { patchSetDocumentsStatusCache } from 'src/components/reports/report-detail/grid-document-status';
import type { DetectedSignatureField } from 'src/components/viewer/signature-field-navigation';
import {
  getFieldCountsAsSigned,
  getPresentableSignatureFields,
  scrollToSignatureField,
} from 'src/components/viewer/signature-field-navigation';
import { useSignatureFieldHighlight } from 'src/components/viewer/use-signature-field-highlight';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDispatchInfoSnack } from 'src/helpers';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { getDigitalSignatureCards } from './digital-signature-cards';
import type { SummaryDocument } from './DocumentSummaryDrawer';
import { Section } from './DocumentSummarySection';
import { getSignerSignatureTargets } from './signer-signature-target';

const useDetectedSignaturesStyles = makeStyles()(theme => ({
  aiNote: {
    alignItems: 'center',
    backgroundColor: '#146EE80A',
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1, 0.5, 1.5),
  },
  aiNoteText: {
    flex: 1,
    minWidth: 0,
  },
  card: {
    '&:disabled': {
      cursor: 'default',
    },
    '&:hover:not(:disabled)': {
      borderColor: theme.palette.primary.main,
    },
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    font: 'inherit',
    gap: theme.spacing(0.25),
    padding: theme.spacing(1, 1.5),
    textAlign: 'left',
    transition: theme.transitions.create(['border-color', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    width: '100%',
  },
  cardHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cardIcon: {
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  cardMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  cardName: {
    flex: 1,
    fontWeight: 600,
    minWidth: 0,
  },
  cardRole: {
    ...createClampStyles(2),
    color: theme.palette.text.secondary,
  },
  countChip: {
    '& .MuiChip-label': {
      color: '#146EE8',
      fontFamily: '"Open Sans", sans-serif',
      fontSize: 14,
      fontWeight: 700,
      paddingLeft: 0,
      paddingRight: 0,
    },
    backgroundColor: '#C0D5F1',
    borderRadius: 4,
    height: 20,
    marginLeft: 16,
    minWidth: 20,
    width: 20,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  pageChip: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    flexShrink: 0,
    fontSize: 12,
    height: 20,
  },
  selectedCard: {
    backgroundColor: '#146EE80A',
    borderColor: theme.palette.primary.main,
  },
  staticCard: {
    '&&&': {
      cursor: 'default',
    },
    '&&&:hover': {
      borderColor: theme.palette.divider,
    },
  },
  status: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  undoButton: {
    flexShrink: 0,
    textTransform: 'none',
  },
  warningIcon: {
    color: theme.palette.warning.main,
    flexShrink: 0,
  },
}));

/**
 * One card in the section: a signer the AI read, or a detected signature box
 * when signer extraction has nothing to say. `field` is the box the card
 * jumps to; null when the card has nowhere to land.
 */
interface DetectedSignatureCard {
  field: DetectedSignatureField | null;
  key: string;
  meta: string;
  name: string;
  pageNumber: number | null;
  role: string;
}

const SignatureCardButton = React.memo(function SignatureCardButton({
  card,
  disabled,
  onToggle,
  selected,
}: {
  card: DetectedSignatureCard;
  disabled: boolean;
  onToggle: (card: DetectedSignatureCard) => void;
  selected: boolean;
}): JSX.Element {
  const { classes } = useDetectedSignaturesStyles();

  return (
    <button
      aria-label={card.field ? `Go to ${card.name}'s signature` : undefined}
      aria-pressed={selected}
      className={clsx(classes.card, selected && classes.selectedCard)}
      disabled={disabled}
      type="button"
      onClick={() => onToggle(card)}
    >
      <div className={classes.cardHeader}>
        <Signature className={classes.cardIcon} size={16} />
        <Typography noWrap className={classes.cardName} variant="body1">
          {card.name}
        </Typography>
        {card.pageNumber !== null && (
          <Chip className={classes.pageChip} label={`Page ${card.pageNumber}`} size="small" />
        )}
      </div>
      {Boolean(card.role) && (
        <Typography className={classes.cardRole} variant="body2">
          {card.role}
        </Typography>
      )}
      {Boolean(card.meta) && (
        <Typography className={classes.cardMeta} variant="body2">
          {card.meta}
        </Typography>
      )}
    </button>
  );
});

/**
 * What signature detection found on the latest version, and the status it set.
 * One card per signer (or per signature box when no signer was read). Clicking
 * a signer card scrolls the viewer to that signature and outlines it. A card
 * per digital signature says who signed and whether the file has changed
 * since; it has no place on the page to jump to. In repository preview there is no viewer, so the signer cards are
 * read-only.
 */
export function DetectedSignatures({
  dividerClassName,
  document,
  documentId,
}: {
  dividerClassName: string;
  document: SummaryDocument;
  documentId: UUID;
}): JSX.Element | null {
  const { classes } = useDetectedSignaturesStyles();
  const currentUser = useSelectCurrentUser();
  const documentViewer = useMaybeDocumentViewer();
  const [expanded, setExpanded] = useState(true);
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [setDocumentsStatus, { loading: undoing }] = Apollo.useSetDocumentsStatusMutation();

  const staticFileVersion =
    document?.latestVersion.__typename === 'StaticFileVersion' ? document.latestVersion : null;
  const detection = staticFileVersion?.signatureDetection ?? null;
  const fields = useMemo(
    () => getPresentableSignatureFields(detection?.detectedSignatureFields ?? []),
    [detection?.detectedSignatureFields]
  );
  const cards = useMemo(() => getDetectedSignatureCards(detection, fields), [detection, fields]);
  const digitalCards = useMemo(() => getDigitalSignatureCards(detection), [detection]);
  const selectedCard = cards.find(card => card.key === selectedKey) ?? null;
  useSignatureFieldHighlight(documentViewer, selectedCard?.field ?? null);

  /**
   * A new version brings new boxes; an outline left on the old one would point
   * at nothing. Compared against a ref so a hidden tab coming back (same
   * version) keeps the selection.
   */
  const latestVersionId = document?.latestVersion.id;
  const selectedForVersionRef = useRef(latestVersionId);
  useEffect(() => {
    if (selectedForVersionRef.current === latestVersionId) return;
    selectedForVersionRef.current = latestVersionId;
    setSelectedKey(null);
  }, [latestVersionId]);

  const dispatchNotFoundSnack = useDispatchInfoSnack({
    message: "Couldn't find this signature in the document.",
  });
  // Clicking the selected card again clears the outline.
  const toggleCard = useCallback(
    (card: DetectedSignatureCard) => {
      if (card.key === selectedKey) setSelectedKey(null);
      else if (card.field && scrollToSignatureField(documentViewer, card.field))
        setSelectedKey(card.key);
      else dispatchNotFoundSnack();
    },
    [dispatchNotFoundSnack, documentViewer, selectedKey]
  );

  const handleUndo = useCallback(async () => {
    await setDocumentsStatus({
      update(cache, result) {
        const payload = result.data?.setDocumentsStatus;
        if (payload) patchSetDocumentsStatusCache(cache, payload);
      },
      variables: { input: { documentIds: [documentId], status: Apollo.DocumentStatus.Draft } },
    });
  }, [documentId, setDocumentsStatus]);

  if (!currentUser?.org.signatureDetectionEnabled) return null;
  if (!document) return null;

  // Same rule as the grid's Status cell, so the two say "detecting" together.
  const isRunning = getIsSignatureDetectionPending({
    detection,
    document,
    isEnabled: true,
    isPdf: staticFileVersion?.fileLocation.mimetype === MimeType.PDF,
  });
  if (!detection?.startedAt && !isRunning) return null;

  const hasCryptographicSignature = Boolean(detection?.hasCryptographicSignature);
  /**
   * From the detection itself, not the drawable boxes: a box whose rect cannot
   * be drawn still counts, or a document that was set to Signed would read
   * "No signatures found" and lose its Undo.
   */
  const isDetected =
    Boolean(detection?.finishedAt) &&
    (hasCryptographicSignature ||
      Boolean(detection?.detectedSignatureFields.some(getFieldCountsAsSigned)));
  const count = cards.length + digitalCards.length;
  // Same rule as the grid's status menu: only an editor can change the status back.
  const canEditStatus = document.requesterRole === Apollo.PermissionRole.Editor;

  return (
    <>
      <Divider className={dividerClassName} />
      <Section
        headerChildren={
          <>
            {isDetected && <Chip className={classes.countChip} label={count} size="small" />}
            <Spacer />
            <AlineTooltip arrow placement="top" title={expanded ? 'Collapse' : 'Expand'}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                <AnimatedArrowIcon rotated={expanded} />
              </IconButton>
            </AlineTooltip>
          </>
        }
        title="Detected Signatures"
      >
        <Collapse in={expanded}>
          {isRunning && (
            <div className={classes.status}>
              <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
              <Typography variant="body1">Aline AI is detecting signatures...</Typography>
            </div>
          )}
          {!isRunning && Boolean(detection?.failedAt) && (
            <Typography color="textSecondary" variant="body2">
              Aline AI could not read this PDF for signatures.
            </Typography>
          )}
          {!isRunning && Boolean(detection?.finishedAt) && !isDetected && (
            <Typography color="textSecondary" variant="body2">
              No signatures found.
            </Typography>
          )}
          {isDetected && (
            <div className={classes.list}>
              {digitalCards.map(card => (
                <div key={card.key} className={clsx(classes.card, classes.staticCard)}>
                  <div className={classes.cardHeader}>
                    {card.isValid ? (
                      <ShieldCheck className={classes.cardIcon} size={16} />
                    ) : (
                      <ShieldAlert className={classes.warningIcon} size={16} />
                    )}
                    <Typography noWrap className={classes.cardName} variant="body1">
                      {card.name}
                    </Typography>
                  </div>
                  {Boolean(card.role) && (
                    <Typography className={classes.cardRole} variant="body2">
                      {card.role}
                    </Typography>
                  )}
                  {Boolean(card.meta) && (
                    <Typography className={classes.cardMeta} variant="body2">
                      {card.meta}
                    </Typography>
                  )}
                </div>
              ))}
              {cards.map(card => (
                <SignatureCardButton
                  key={card.key}
                  card={card}
                  disabled={!documentViewer || !card.field}
                  selected={card.key === selectedKey}
                  onToggle={toggleCard}
                />
              ))}
              {document.isSigned && document.isSignedAiGenerated && (
                <div className={classes.aiNote}>
                  <SmallAiSparkleIcon />
                  <Typography className={classes.aiNoteText} color="textSecondary" variant="body2">
                    Status set to Signed by Aline AI.
                  </Typography>
                  {canEditStatus && (
                    <Button
                      className={classes.undoButton}
                      disabled={undoing}
                      size="small"
                      variant="text"
                      onClick={() => setUndoConfirmOpen(true)}
                    >
                      Undo
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </Collapse>
      </Section>
      <ConfirmationDialog
        message="This sets the document back to Draft."
        open={undoConfirmOpen}
        primaryText="Undo"
        title="Undo signed status?"
        onClickPrimary={handleUndo}
        onClose={() => setUndoConfirmOpen(false)}
      />
    </>
  );
}

/**
 * A card per signer when the AI read any, otherwise one per detected box. Each
 * card jumps where `getSignerSignatureTargets` says, or nowhere.
 */
function getDetectedSignatureCards(
  detection: Apollo.SignatureDetectionDetailsFragment | null | undefined,
  fields: DetectedSignatureField[]
): DetectedSignatureCard[] {
  const signers = detection?.signers ?? [];

  if (signers.length) {
    const targets = getSignerSignatureTargets(signers, fields);
    return signers.map((signer, index) => {
      const field = targets[index] ?? null;
      const meta = [
        signer.signedDate && `Signed ${formatSignedDate(signer.signedDate)}`,
        `${Math.round(signer.confidence * 100)}% confidence`,
      ].filter(Boolean);
      return {
        field,
        key: `signer-${index}`,
        meta: meta.join(' · '),
        name: signer.name,
        pageNumber: signer.pageNumber ?? field?.pageNumber ?? null,
        role: [signer.title, signer.organization].filter(Boolean).join(', '),
      };
    });
  }

  return fields.map((field, index) => ({
    field,
    key: `field-${index}`,
    meta: '',
    name: `Signature ${index + 1}`,
    pageNumber: field.pageNumber,
    role: '',
  }));
}

/** `2024-05-09` as "May 9, 2024", read as a calendar date so no timezone shifts it. */
function formatSignedDate(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  });
}
